<template>
  <div class="grid grid-cols-1 items-start gap-5 lg:grid-cols-[252px_minmax(0,1fr)]">
    <!--
      The fixed left index (prototype v3 §6.9): every finding with its severity
      icon, sticky under the top bar and scrolling inside itself, filtered in
      step with the list. Below lg it drops above the list instead of vanishing.
    -->
    <aside
      class="qc-index overflow-hidden rounded-lg border border-gray-200 bg-white pb-2 shadow-xs lg:sticky lg:overflow-y-auto"
      :aria-label="$t('messages.evaluation.qualityCheck.results.listTitle')"
      data-testid="qc-index"
    >
      <h4 class="mb-1 flex items-center gap-2 bg-gray-100 px-3 py-2.5 text-xs font-medium uppercase tracking-wider text-gray-500">
        <span class="pi pi-list" aria-hidden="true"></span>
        {{ $t('messages.evaluation.qualityCheck.results.indexTitle', { n: visibleLeids.length }) }}
      </h4>
      <p v-if="visibleLeids.length === 0" class="px-3 py-2 text-sm text-gray-400">
        {{ $t('messages.evaluation.qualityCheck.results.filterEmpty') }}
      </p>
      <ol v-else>
        <li v-for="finding in visibleLeids" :key="`idx-${finding.id}`">
          <button
            type="button"
            class="flex w-full items-start gap-2 border-l-[3px] border-transparent px-3 py-1.5 text-left text-sm leading-snug text-gray-700 transition-colors hover:bg-gray-100"
            data-testid="qc-index-item"
            @click="jumpTo(finding.id)"
          >
            <span class="pi mt-0.5 shrink-0" :class="[severityOf(finding).icon, severityOf(finding).colour]" aria-hidden="true"></span>
            <span class="min-w-0 flex-1" :class="{ 'text-gray-400 line-through': finding.resolution }">{{ finding.title }}</span>
          </button>
        </li>
      </ol>
      <template v-if="vabaFindings.length && activeSeverity === null">
        <p class="mt-1.5 border-t border-gray-100 px-3 pb-1 pt-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">
          {{ $t('messages.evaluation.qualityCheck.results.vabaTitle') }}
        </p>
        <ol>
          <li v-for="finding in vabaFindings" :key="`idx-${finding.id}`">
            <button
              type="button"
              class="flex w-full items-start gap-2 border-l-[3px] border-transparent px-3 py-1.5 text-left text-sm leading-snug text-gray-700 transition-colors hover:bg-gray-100"
              data-testid="qc-index-item"
              @click="jumpTo(finding.id)"
            >
              <span class="pi pi-lightbulb mt-0.5 shrink-0 text-gray-400" aria-hidden="true"></span>
              <span class="min-w-0 flex-1" :class="{ 'text-gray-400 line-through': finding.resolution }">{{ finding.title }}</span>
            </button>
          </li>
        </ol>
      </template>
    </aside>

    <div class="min-w-0">
      <!--
        One row, three BUTTONS: clicking one filters the list AND the index;
        clicking it again clears the filter. Severity renders as an icon and a
        number, never a saturated fill (§17.1 / AC 28). A tile with nothing
        behind it is disabled rather than a dead click.
      -->
      <div class="mb-4 grid grid-cols-1 gap-2.5 sm:grid-cols-3" data-testid="qc-tiles">
        <button
          v-for="tile in tiles"
          :key="tile.severity"
          type="button"
          class="flex w-full items-center gap-2.5 rounded-lg border bg-white px-4 py-3 text-left shadow-xs transition-colors disabled:cursor-default disabled:opacity-50"
          :class="activeSeverity === tile.severity
            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/50'
            : 'border-gray-200 enabled:hover:border-blue-300 enabled:hover:bg-gray-50'"
          :disabled="tile.count === 0"
          :aria-pressed="activeSeverity === tile.severity"
          :data-severity="tile.severity"
          data-testid="qc-tile"
          @click="toggleSeverity(tile.severity)"
        >
          <span class="pi shrink-0 text-lg" :class="[tile.icon, tile.colour]" aria-hidden="true"></span>
          <span class="text-2xl font-semibold leading-none tabular-nums" :class="tile.colour">{{ tile.count }}</span>
          <span class="text-sm leading-tight text-gray-500">{{ tile.label }}</span>
        </button>
      </div>

      <div v-if="activeSeverity !== null" class="-mt-1 mb-3 flex items-center gap-2.5 text-sm text-gray-500" data-testid="qc-filter-bar">
        <span>
          {{ $t('messages.evaluation.qualityCheck.results.showing') }}
          <strong class="font-semibold text-gray-700">{{ $t(`messages.evaluation.qualityCheck.severity.${slugOf(activeSeverity)}`) }}</strong>
        </span>
        <Button
          :label="$t('messages.evaluation.qualityCheck.results.showAll')"
          icon="pi pi-times"
          severity="secondary"
          text
          size="small"
          class="w-auto!"
          data-testid="qc-filter-clear"
          @click="activeSeverity = null"
        />
      </div>

      <div class="mb-2 flex items-baseline gap-2.5">
        <h2 class="text-lg font-semibold text-gray-800">{{ $t('messages.evaluation.qualityCheck.results.listTitle') }}</h2>
        <span class="text-sm text-gray-500" data-testid="qc-sorted-label">{{ hasPlaces
          ? $t('messages.evaluation.qualityCheck.results.sortedWithPlace')
          : $t('messages.evaluation.qualityCheck.results.sorted') }}</span>
      </div>

      <div
        v-if="visibleLeids.length === 0"
        class="mb-2 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500 shadow-xs"
        data-testid="qc-filter-empty"
      >
        {{ $t('messages.evaluation.qualityCheck.results.filterEmpty') }}
      </div>
      <QcFindingCard
        v-for="finding in visibleLeids"
        :key="finding.id"
        :finding="finding"
        :open="openIds.has(finding.id)"
        :busy="resolvingId === finding.id"
        :file-deleted="fileDeleted"
        :checklist-text="checklistText"
        :send-feedback="readOnly ? null : sendFeedback"
        :clear-feedback="readOnly ? null : clearFeedback"
        :read-only="readOnly"
        @toggle="toggleOpen"
        @resolve="onResolve"
      />

      <!-- `vaba` rows: prose with no severity marker and no checklist item
           (design-spec §4), kept apart from the ranked list. -->
      <template v-if="vabaFindings.length && activeSeverity === null">
        <div class="mb-2 mt-6">
          <h3 class="text-sm font-semibold text-gray-800">{{ $t('messages.evaluation.qualityCheck.results.vabaTitle') }}</h3>
          <p class="mt-0.5 text-sm text-gray-500">{{ $t('messages.evaluation.qualityCheck.results.vabaSub') }}</p>
        </div>
        <QcFindingCard
          v-for="finding in vabaFindings"
          :key="finding.id"
          :finding="finding"
          :open="openIds.has(finding.id)"
          :busy="resolvingId === finding.id"
          :file-deleted="fileDeleted"
          :checklist-text="checklistText"
          :send-feedback="readOnly ? null : sendFeedback"
          :clear-feedback="readOnly ? null : clearFeedback"
          :read-only="readOnly"
          @toggle="toggleOpen"
          @resolve="onResolve"
        />
      </template>

      <!--
        `Mida masin kontrollis` and `Kuidas tähelepanekuid lugeda` live BELOW
        the findings, collapsed, under a quiet label — Toomas, RED-340 comment
        30633: they must not stand between the appraiser and the findings.
      -->
      <div class="mt-8 border-t border-gray-200 pt-4" data-testid="qc-more">
        <p class="mb-2.5 text-xs font-medium uppercase tracking-wider text-gray-400">
          {{ $t('messages.evaluation.qualityCheck.results.more') }}
        </p>
        <QcChecklistPanel
          :checklist="checklist"
          :findings="findings"
          :checks-total="checksTotal"
          @jump="jumpTo"
        />
        <QcHowTo :checks-total="checksTotal" :support-email="supportEmail" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import QcChecklistPanel from './QcChecklistPanel.vue'
import QcFindingCard from './QcFindingCard.vue'
import QcHowTo from './QcHowTo.vue'

/**
 * RED-351 rework — the findings screen of prototype v3: the sticky index, the
 * three severity tiles, the filter line, the ranked `leid` list, the `vaba`
 * section, and `Lisainfo` at the foot.
 */
const props = defineProps({
  findings: { type: Array, default: () => [] },
  checklist: { type: Array, default: () => [] },
  checksTotal: { type: Number, default: 22 },
  supportEmail: { type: String, required: true },
  /** The file is gone: cards say so instead of quoting the report. */
  fileDeleted: { type: Boolean, default: false },
  /**
   * `useReportCheck().resolveFinding` — resolves to true when the server took
   * it. Awaited here so a card only collapses on a SAVED answer.
   */
  resolve: { type: Function, default: null },
  /** RED-388: `useReportCheck().sendFeedback` / `clearFeedback`, handed to each card. */
  sendFeedback: { type: Function, default: null },
  clearFeedback: { type: Function, default: null },
  /**
   * RED-389: the REdata admin's read-only view of another appraiser's run.
   * Cards show no resolution button, no undo and no feedback row; the
   * filters, the index and the checklist still work (they change nothing).
   */
  readOnly: { type: Boolean, default: false },
})

const { t } = useI18n()

const SEVERITIES = [
  { severity: 'blokeeriv', slug: 'blokeeriv', icon: 'pi-ban', colour: 'text-red-600' },
  { severity: 'vajab parandust', slug: 'vajabParandust', icon: 'pi-exclamation-triangle', colour: 'text-amber-500' },
  { severity: 'soovituslik', slug: 'soovituslik', icon: 'pi-lightbulb', colour: 'text-gray-500' },
]

/**
 * RED-373 (provisional): a finding the machine gave no severity counts as
 * `soovituslik` on this screen — in its tile, its filter and the index — so
 * the three tiles always add up to the list. The stored row is unchanged.
 */
const severityKey = finding => (SEVERITIES.some(s => s.severity === finding.severity) ? finding.severity : 'soovituslik')

const severityOf = finding => SEVERITIES.find(s => s.severity === severityKey(finding))
const slugOf = severity => SEVERITIES.find(s => s.severity === severity)?.slug ?? 'soovituslik'

const activeSeverity = ref(null)
const openIds = ref(new Set())
const resolvingId = ref(null)

const leidFindings = computed(() => props.findings.filter(f => f.type !== 'vaba'))
const vabaFindings = computed(() => props.findings.filter(f => f.type === 'vaba'))

// RED-372: the server sorts by severity, then place in the report — say so once a place exists.
const hasPlaces = computed(() => props.findings.some(f => Number.isInteger(f.excerpt?.offset)))

const visibleLeids = computed(() =>
  activeSeverity.value === null
    ? leidFindings.value
    : leidFindings.value.filter(f => severityKey(f) === activeSeverity.value),
)

/** RED-373: checkpoint number → full text, for the hover on each card's numbers. */
const checklistText = computed(() =>
  Object.fromEntries((props.checklist ?? []).map(item => [String(item.no), item.full || item.short || ''])),
)

const tiles = computed(() =>
  SEVERITIES.map(s => ({
    ...s,
    count: leidFindings.value.filter(f => severityKey(f) === s.severity).length,
    label: t(`messages.evaluation.qualityCheck.tiles.${s.slug}`),
  })),
)

const toggleSeverity = severity => {
  activeSeverity.value = activeSeverity.value === severity ? null : severity
}

const setOpen = (findingId, open) => {
  const next = new Set(openIds.value)
  if (open) {
    next.add(findingId)
  } else {
    next.delete(findingId)
  }
  openIds.value = next
}

const toggleOpen = findingId => setOpen(findingId, !openIds.value.has(findingId))

/**
 * A jump always beats a filter: drop a filter that hides the target FIRST,
 * then open and scroll (AC 30). Jumping while a filter hid the target was a
 * real bug in the prototype's own browser pass.
 */
const jumpTo = async findingId => {
  const target = props.findings.find(f => f.id === findingId)
  if (target && activeSeverity.value !== null && severityKey(target) !== activeSeverity.value) {
    activeSeverity.value = null
  }
  setOpen(findingId, true)
  await nextTick()
  document.getElementById(`qc-finding-${findingId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

/**
 * The PATCH goes to the server; the card collapses only once it is saved
 * (resolving collapses, undoing reopens — §16.6). The busy flag lives here,
 * not in the card, so a failed request can never leave it stuck.
 */
const onResolve = async (findingId, resolution) => {
  if (props.readOnly || !props.resolve) {
    return
  }
  resolvingId.value = findingId
  try {
    const saved = await props.resolve(findingId, resolution)
    if (saved) {
      setOpen(findingId, resolution === null)
    }
  } finally {
    resolvingId.value = null
  }
}
</script>

<style scoped>
/* Sticky under the navbar (4rem) and the page's own top bar; the page sets
   --qc-head-h from the bar's measured height. */
@media (min-width: 64rem) {
  .qc-index {
    top: calc(4rem + var(--qc-head-h, 5rem) + 0.75rem);
    max-height: calc(100vh - 4rem - var(--qc-head-h, 5rem) - 1.5rem);
  }
}

:deep(.qc-finding) {
  scroll-margin-top: calc(4rem + var(--qc-head-h, 5rem) + 0.75rem);
}
</style>
